import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import { clientsAPI, projectsAPI, tasksAPI } from '../services/api';

export default function Analytics() {
  const [tabValue, setTabValue] = useState(0);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const clientStatuses = ['Активный', 'В разработке', 'Закрыт', 'Приостановлен'];
  const projectStatuses = ['развитие с нуля', 'развитие покупают', 'регулярный успешный', 'угасает', 'закрыт'];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [clientsRes, projectsRes, tasksRes] = await Promise.all([
        clientsAPI.getAll(),
        projectsAPI.getAll(),
        tasksAPI.getAll()
      ]);
      setClients(clientsRes.data);
      setProjects(projectsRes.data);
      setTasks(tasksRes.data);
      if (clientsRes.data.length > 0) {
        setSelectedClient(clientsRes.data[0].id);
      }
      if (projectsRes.data.length > 0) {
        setSelectedProject(projectsRes.data[0].id);
      }
    } catch (err) {
      setError('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const getClientProjects = () => {
    return projects.filter(p => p.clientId === selectedClient);
  };

  const getClientTasks = () => {
    const clientProjects = getClientProjects();
    const projectIds = clientProjects.map(p => p.id);
    return tasks.filter(t => projectIds.includes(t.projectId));
  };

  const getProjectTasks = () => {
    return tasks.filter(t => t.projectId === selectedProject);
  };

  const getProjectClient = () => {
    const project = projects.find(p => p.id === selectedProject);
    return clients.find(c => c.id === project?.clientId);
  };

  const getStatusCounts = (taskList) => {
    const counts = {};
    taskList.forEach(task => {
      const statusName = task.status?.name || 'Без статуса';
      counts[statusName] = (counts[statusName] || 0) + 1;
    });
    return counts;
  };

  const getClientStatusCounts = () => {
    const counts = {};
    clients.forEach(client => {
      const status = client.status || 'Без статуса';
      counts[status] = (counts[status] || 0) + 1;
    });
    return counts;
  };

  const getProjectStatusCounts = () => {
    const counts = {};
    projects.forEach(project => {
      const status = project.status || 'Без статуса';
      counts[status] = (counts[status] || 0) + 1;
    });
    return counts;
  };

  const getTaskStatusCounts = () => {
    const counts = {};
    tasks.forEach(task => {
      const status = task.status?.name || 'Без статуса';
      counts[status] = (counts[status] || 0) + 1;
    });
    return counts;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Аналитика
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Общая статистика */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                Клиенты по статусам
              </Typography>
              <List dense>
                {Object.entries(getClientStatusCounts()).map(([status, count]) => (
                  <ListItem key={status}>
                    <ListItemText
                      primary={status}
                      secondary={`${count} ${count === 1 ? 'клиент' : 'клиентов'}`}
                    />
                  </ListItem>
                ))}
              </List>
              <Typography variant="h4" color="primary" align="center" mt={1}>
                Всего: {clients.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="success.main">
                Проекты по статусам
              </Typography>
              <List dense>
                {Object.entries(getProjectStatusCounts()).map(([status, count]) => (
                  <ListItem key={status}>
                    <ListItemText
                      primary={status}
                      secondary={`${count} ${count === 1 ? 'проект' : 'проектов'}`}
                    />
                  </ListItem>
                ))}
              </List>
              <Typography variant="h4" color="success.main" align="center" mt={1}>
                Всего: {projects.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="secondary">
                Задачи по статусам
              </Typography>
              <List dense>
                {Object.entries(getTaskStatusCounts()).map(([status, count]) => (
                  <ListItem key={status}>
                    <ListItemText
                      primary={status}
                      secondary={`${count} ${count === 1 ? 'задача' : 'задач'}`}
                    />
                  </ListItem>
                ))}
              </List>
              <Typography variant="h4" color="secondary" align="center" mt={1}>
                Всего: {tasks.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="По клиентам" />
          <Tab label="По проектам (темам)" />
        </Tabs>
      </Box>

      {tabValue === 0 && (
        <Box>
          {clients.length === 0 ? (
            <Alert severity="info">Нет клиентов для аналитики</Alert>
          ) : (
            <>
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <TextField
                    select
                    label="Выберите клиента"
                    value={selectedClient}
                    onChange={(e) => setSelectedClient(e.target.value)}
                    fullWidth
                  >
                    {clients.map(c => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.companyName}
                      </MenuItem>
                    ))}
                  </TextField>
                </CardContent>
              </Card>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Проекты клиента
                      </Typography>
                      <Typography variant="h3" color="primary" mb={2}>
                        {getClientProjects().length}
                      </Typography>
                      <List dense>
                        {getClientProjects().map(project => (
                          <ListItem key={project.id}>
                            <ListItemText primary={project.name} />
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Задачи клиента
                      </Typography>
                      <Typography variant="h3" color="primary" mb={2}>
                        {getClientTasks().length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        По статусам:
                      </Typography>
                      <List dense>
                        {Object.entries(getStatusCounts(getClientTasks())).map(([status, count]) => (
                          <ListItem key={status}>
                            <ListItemText
                              primary={status}
                              secondary={`${count} ${count === 1 ? 'задача' : 'задач'}`}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </>
          )}
        </Box>
      )}

      {tabValue === 1 && (
        <Box>
          {projects.length === 0 ? (
            <Alert severity="info">Нет проектов для аналитики</Alert>
          ) : (
            <>
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <TextField
                    select
                    label="Выберите проект (тему)"
                    value={selectedProject}
                    onChange={(e) => setSelectedProject(e.target.value)}
                    fullWidth
                  >
                    {projects.map(p => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.name} ({p.client?.companyName})
                      </MenuItem>
                    ))}
                  </TextField>
                </CardContent>
              </Card>

              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Клиент проекта
                      </Typography>
                      <Typography variant="h5" color="primary">
                        {getProjectClient()?.companyName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mt={1}>
                        Статус: {getProjectClient()?.status}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={8}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Задачи проекта
                      </Typography>
                      <Typography variant="h3" color="primary" mb={2}>
                        {getProjectTasks().length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        По статусам:
                      </Typography>
                      <List dense>
                        {Object.entries(getStatusCounts(getProjectTasks())).map(([status, count]) => (
                          <ListItem key={status}>
                            <ListItemText
                              primary={status}
                              secondary={`${count} ${count === 1 ? 'задача' : 'задач'}`}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </>
          )}
        </Box>
      )}
    </Box>
  );
}
